import { useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  createAttributeHold,
  createMaintenanceHold,
  createPredicateHold,
  deleteHold,
  getHeldWorkflows,
  getHoldRules,
  recheckHolds,
  SUBSCRIBERS_MAX_PAGE_SIZE,
} from '@/console/api/holds'
import {
  AttributeHoldRequest,
  HeldPage,
  HeldWorkflowView,
  HoldRuleView,
  MaintenanceHoldRequest,
  PredicateHoldRequest,
  RecheckResult,
} from '@/console/api/types'
import { consoleQueryClient } from '@/console/queryClient'
import { PanelState, toPanelState } from '@/console/state/panelState'

/**
 * LOGISPRD-12506 Task 13 — Dispatcher 탭이 쓰는 조회 훅. 1단계 목데이터를 실제 API 호출로 교체한다.
 *
 * Task 13 리뷰(코디네이터 후속 지시 1·2건)로 두 가지가 바뀌었다:
 *  1. **StrictMode 이중발화 가드.** `DispatcherTab.tsx` 는 폼 제출처럼 조회를 거는 버튼이 없어
 *     훅 내부에서 마운트 시 `refetch()` 를 스스로 한 번 건다 — `queryClient.ts` 의 `enabled:false`
 *     는 React Query 표준 자동 재조회(포커스·재연결·큐리키 변경)만 막을 뿐 이 명시적 1회 호출까지
 *     막지 않는다. 문제는 React 18 dev `StrictMode`(`index.tsx`)가 이 마운트 effect 를
 *     mount→cleanup→mount 로 두 번 실행해 같은 요청이 2배 나간다는 것 — `hasAutoFetchedRef` 로
 *     "이 컴포넌트 인스턴스에서 이미 한 번 걸었다"를 기억해 두 번째 실행을 막는다. `useRef` 는
 *     StrictMode 의 synthetic 재마운트에서도(같은 fiber 라) 값을 유지하고, 탭을 나갔다가 실제로
 *     다시 들어와 컴포넌트가 진짜로 새로 마운트되면(`ConsoleShell` 이 `{tab==='disp' && <DispatcherTab/>}`
 *     로 언마운트한다) 새 `useRef` 가 만들어져 정상적으로 다시 조회된다 — 프로덕션 동작(탭 진입
 *     시 1회 조회)은 그대로다.
 *  2. **시그니처 변경.** 기존 `(): PanelState<T>` 는 `refetch` 를 화면에 노출할 방법이 없어
 *     "탭을 나갔다 들어와야만 재조회된다"는 문제가 있었다. `{ state: PanelState<T>; refetch: () => void }`
 *     로 바꿔 화면이 명시적 재조회 버튼을 만들 수 있게 했다(`DispatcherTab.tsx` 의 "새로고침"
 *     버튼, `SectionHeading` 의 `right` 슬롯). 이후 조회 훅(Task 14~18)이 재조회 버튼을 필요로
 *     하면 이 `{ state, refetch }` 형태를 선례로 참고할 수 있다 — 다만 강제하지는 않는다(각
 *     도메인 화면이 재조회 버튼을 필요로 하는지는 그 태스크가 판단할 몫).
 */
const HOLD_RULES_QUERY_KEY = ['console', 'holds', 'rules'] as const
const HELD_WORKFLOWS_QUERY_KEY = ['console', 'holds', 'subscribers'] as const

export type HoldRulesQuery = { state: PanelState<HoldRuleView[]>; refetch: () => void }
/**
 * `hasMore` — subscribers 응답의 `nextPageToken` 이 남아 있는지(PR 리뷰). 화면에 페이지네이션
 * UI 가 없어 1페이지(최대 200건)만 보여주는데, 예전에는 토큰을 그냥 버려서 201건째부터는
 * **화면에서 사라지고 그 사실조차 알 수 없었다.** 온콜 중 "멈춘 워크플로가 이게 전부"라고 믿는
 * 것이 위험해서, 최소한 더 있다는 사실은 화면이 말하게 한다.
 */
export type HeldWorkflowsQuery = { state: PanelState<HeldWorkflowView[]>; hasMore: boolean; refetch: () => void }

export const useHoldRules = (): HoldRulesQuery => {
  const query = useQuery({ queryKey: HOLD_RULES_QUERY_KEY, queryFn: getHoldRules, enabled: false })
  const { refetch } = query
  const hasAutoFetchedRef = useRef(false)

  useEffect(() => {
    if (hasAutoFetchedRef.current) {
      return
    }
    hasAutoFetchedRef.current = true
    refetch().catch(() => undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 마운트 시 1회만(가드로 StrictMode 이중발화 방지). refetch 는 매 렌더 안정적이다.
  }, [])

  return {
    state: toPanelState(query, '등록된 Hold 규칙이 없어요.', (rules) => rules.length === 0),
    refetch: () => {
      refetch().catch(() => undefined)
    },
  }
}

export const useHeldWorkflows = (): HeldWorkflowsQuery => {
  const query = useQuery({
    queryKey: HELD_WORKFLOWS_QUERY_KEY,
    // §4.1 — subscribers 는 HeldPage(items + nextPageToken) 를 준다. 화면에 페이지네이션 UI 가
    // 없어 1페이지(최대 pageSize=200)만 그리지만, 응답은 **통째로** 캐시에 둔다 — `nextPageToken`
    // 이 있는지를 화면이 알아야 "더 있음" 을 표시할 수 있다(PR 리뷰). 조치 후 재조회
    // (`refetchHeldWorkflows`)와 반드시 같은 queryFn 을 써야 한다 — 캐시 값 모양이 갈리면 조치
    // 성공 직후 목록이 통째로 사라진다(PR 리뷰 High).
    queryFn: fetchHeldWorkflowsPage,
    enabled: false,
  })
  const { refetch } = query
  const hasAutoFetchedRef = useRef(false)

  useEffect(() => {
    if (hasAutoFetchedRef.current) {
      return
    }
    hasAutoFetchedRef.current = true
    refetch().catch(() => undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    // `toPanelState` 는 5개 필드만 보므로 data 만 items 로 갈아끼운 리터럴을 넘긴다 —
    // 화면이 보는 상태 타입(`PanelState<HeldWorkflowView[]>`)은 그대로 유지된다.
    state: toPanelState(
      { isFetching: query.isFetching, isError: query.isError, error: query.error, data: query.data?.items, isFetched: query.isFetched },
      '정책 Hold 로 멈춘 워크플로가 없어요.',
      (workflows) => workflows.length === 0,
    ),
    hasMore: Boolean(query.data?.nextPageToken),
    refetch: () => {
      refetch().catch(() => undefined)
    },
  }
}

/**
 * §4.1 함정 — 삭제 응답에 본문이 없어 서버가 규칙 값을 돌려주지 않는다. "삭제를 되돌리기"(재등록)를
 * 하려면 삭제 직전 화면이 들고 있던 rule 값이 필요하므로, 삭제 함수가 호출될 때마다 그 값을 모듈
 * 스코프에 스냅샷으로 남긴다.
 *
 * **되돌리기는 이 스냅샷을 그대로 재등록에 쓸 수 없다(Task 13 리뷰 지적, 정정).** `rule` 은
 * `HoldRuleView`(GET / 응답 DTO)이고 `createHoldRule` 이 요구하는 건 요청 DTO
 * (`MaintenanceHoldRequest`·`AttributeHoldRequest`·`PredicateHoldRequest`)다 — 두 타입은
 * 이름이 겹치는 필드(`from`/`until`/`toExclusive`/`predicate` 등)조차 필수·옵셔널이 다르다.
 * 되돌리기를 실제로 구현하려면 `HoldRuleView → (Maintenance|Attribute|Predicate)HoldRequest`
 * 매핑 함수가 **별도로** 필요하다 — 타입 수준에서 "그대로 재사용"은 불가능하다.
 *
 * 화면 미배선 — 1차 범위엔 되돌리기 UI 자체가 없다(`DispatcherTab.tsx` 는 아직 `deleteHoldRule` 을
 * 부르지 않는다, 1단계의 로컬 add/remove 오버레이만 남아 있다). 그래서 위 매핑 함수는 지금
 * 만들지 않는다 — `getLastDeletedHoldSnapshot()` 은 "방금 지운 규칙이 무엇이었는지" 만 기억해
 * 두는 자리이고, 되돌리기 버튼을 실제로 만드는 태스크가 매핑 함수를 새로 작성해야 한다.
 */
export type HoldRuleSnapshot = { rule: HoldRuleView; deletedAt: number }

let lastDeletedHoldSnapshot: HoldRuleSnapshot | null = null

export const getLastDeletedHoldSnapshot = (): HoldRuleSnapshot | null => lastDeletedHoldSnapshot

/** 테스트 전용 — 모듈 스코프 상태를 초기화한다. */
export const __resetLastDeletedHoldSnapshotForTest = (): void => {
  lastDeletedHoldSnapshot = null
}

const refetchHoldRules = (): Promise<HoldRuleView[]> => consoleQueryClient.fetchQuery({ queryKey: HOLD_RULES_QUERY_KEY, queryFn: getHoldRules })

/**
 * PR 리뷰 High — 같은 캐시 키에 **같은 모양**을 써야 한다.
 *
 * `useHeldWorkflows` 는 `HeldPage`(`{ items, nextPageToken }`)를 통째로 캐시에 두는데(`hasMore`
 * 를 알아야 해서), 이 재조회만 `.items` 배열을 넣고 있었다. "전부 깨우기" 가 성공하면 캐시 값이
 * 배열로 바뀌어 `query.data?.items` 가 `undefined` 가 되고, `toPanelState` 가 `idle` 을 내면서
 * **멈춘 워크플로 목록과 "전부 깨우기" 버튼이 통째로 사라졌다.** 깨운 직후 무엇이 아직 멈춰
 * 있는지 볼 수 없게 되는, 온콜 중 가장 알고 싶은 순간에 화면이 비는 회귀였다.
 *
 * 다시 어긋나지 않도록 queryFn 을 상수 하나로 두고 훅·재조회가 같이 쓴다.
 */
const fetchHeldWorkflowsPage = (): Promise<HeldPage> => getHeldWorkflows({ pageSize: SUBSCRIBERS_MAX_PAGE_SIZE })

const refetchHeldWorkflows = (): Promise<HeldPage> =>
  consoleQueryClient.fetchQuery({ queryKey: HELD_WORKFLOWS_QUERY_KEY, queryFn: fetchHeldWorkflowsPage })

export type CreateHoldRequest =
  | { kind: 'maintenance'; body: MaintenanceHoldRequest }
  | { kind: 'attr'; body: AttributeHoldRequest }
  | { kind: 'predicate'; body: PredicateHoldRequest }

export type CreateHoldResult = {
  /** 서버가 등록 응답으로 돌려준 값 — §4.1 함정: 이게 200 이어도 저장이 확정된 건 아니다. */
  created: HoldRuleView
  /**
   * read-back(`GET /`) 결과. `confirmRegistration:false` 면(화면의 "등록 후 목록 재조회로 반영
   * 확인" 체크 해제) read-back 을 건너뛰고 null 을 반환한다 — 그때는 `created` 만 믿는다.
   */
  confirmed: HoldRuleView[] | null
}

/**
 * 등록 3종(maintenance·attribute·predicate) 공용 진입점. `options.confirmRegistration` 이
 * `HoldCreateForm` 의 "등록 후 목록 재조회로 반영 확인" 체크박스 상태를 그대로 받는 자리다(§4.1
 * 함정 — 등록 200 ≠ 저장 성공).
 */
export const createHoldRule = async (
  request: CreateHoldRequest,
  options: { propagate: boolean; confirmRegistration: boolean },
): Promise<CreateHoldResult> => {
  const created =
    request.kind === 'maintenance'
      ? await createMaintenanceHold(request.body, options.propagate)
      : request.kind === 'attr'
        ? await createAttributeHold(request.body, options.propagate)
        : await createPredicateHold(request.body, options.propagate)

  const confirmed = options.confirmRegistration ? await refetchHoldRules() : null

  return { created, confirmed }
}

/**
 * §4.1 함정 — 삭제는 `propagate=true` 고정(`api/holds.ts` 의 `deleteHold` 가 이미 강제한다).
 * 삭제 전 스냅샷을 남기고, 삭제 후 `GET /` 로 목록을 다시 채워 화면이 보는 캐시를 갱신한다.
 */
export const deleteHoldRule = async (rule: HoldRuleView): Promise<HoldRuleView[]> => {
  lastDeletedHoldSnapshot = { rule, deletedAt: Date.now() }
  await deleteHold(rule.holdId)
  // PR 리뷰 — 뒤따르는 재조회 실패를 조치 실패로 올리지 않는다. 예전에는 삭제가 성공했는데
  // 목록 재조회만 실패해도 화면에 "삭제 실패" 가 떠서, 온콜 담당자가 같은 규칙을 다시 지웠다.
  // 표가 갱신되지 않는 것은 화면의 "새로고침" 버튼으로 복구할 수 있는 상태다.
  return refetchHoldRules().catch(() => [])
}

/** 전부 깨우기(`POST /recheck?domain=`) — 재평가 후 멈춘 워크플로 목록도 함께 새로고침한다. */
export const recheckHoldDomain = async (domain: string): Promise<RecheckResult> => {
  const result = await recheckHolds(domain)
  // PR 리뷰 — 재평가 잡은 이미 만들어졌다. 여기서 재조회 실패를 던지면 화면이 "실패" 를 띄우고
  // 담당자가 다시 눌러 잡이 하나 더 생긴다(`deleteHoldRule` 과 같은 이유).
  await refetchHeldWorkflows().catch(() => undefined)
  return result
}
